import type { ReactNode, RefObject } from 'react'
import { useLayoutEffect, useRef } from 'react'
import { readsRtl, sigAnimate, signatureMoves } from '../../lib/signature/motion'
import { cellsIn } from './kufic-clock'

function arrivalLines(root: HTMLElement): HTMLElement[] {
  const marked = root.querySelectorAll<HTMLElement>('[data-arrival-line]')
  if (marked.length) return Array.from(marked)

  const body =
    root.tagName === 'TBODY'
      ? (root as HTMLTableSectionElement)
      : root.querySelector<HTMLTableSectionElement>('tbody')
  if (body) return Array.from(body.rows)
  return Array.from(root.children) as HTMLElement[]
}

export function useArrivalWrite<T extends HTMLElement = HTMLElement>(
  enabled = true,
): RefObject<T | null> {
  const ref = useRef<T>(null)

  useLayoutEffect(() => {
    const root = ref.current
    if (
      !root ||
      !enabled ||
      typeof Element === 'undefined' ||
      typeof Element.prototype.animate !== 'function'
    )
      return

    if (!signatureMoves()) {
      const fade = sigAnimate(root, [{ opacity: 0 }, { opacity: 1 }], {
        duration: 160,
        fill: 'both',
      })
      return () => fade.cancel()
    }

    const hidden = readsRtl(root) ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)'
    const lines = arrivalLines(root)
    const animations = lines.map((line, index) => {
      const cells = Number(line.dataset.arrivalCells) || cellsIn(line.getBoundingClientRect().width)
      const duration = Math.min(200, cells * 16)
      const delay = Math.min(index * 22, Math.max(0, 300 - duration))
      return sigAnimate(
        line,
        [{ clipPath: hidden, easing: 'steps(12, jump-start)' }, { clipPath: 'inset(0)' }],
        { duration, delay, fill: 'backwards' },
      )
    })
    return () => {
      animations.forEach((animation) => {
        animation.cancel()
      })
    }
  }, [enabled])

  return ref
}

export function ArrivalWrite({
  children,
  enabled = true,
}: {
  children: ReactNode
  enabled?: boolean
}) {
  const ref = useArrivalWrite<HTMLDivElement>(enabled)
  return (
    <div ref={ref} data-arrival-wrapper>
      {children}
    </div>
  )
}
